import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import { Formik, Field, FormikState, FormikProps } from 'formik';
import { Modal as BootstrapModal, Button } from 'react-bootstrap';
import { FormGroup, TextboxField, Modal, SelectDropdownField } from '@tradingblock/components';
import { useStateSelector } from '../../../data/global/dataSelectors';
import { AccountManagementDataActions } from '../../../data/global/actions/AccountManagementActions';
import { getInvalidError, getMismatchError, getRequiredError, getStaleError } from './Validation';
import { AllPhoneTypes, PhoneUpdateValueProps, SubmitPhoneValues } from '@tradingblock/types';

export const PhoneUpdate: React.FunctionComponent<{
  show?: boolean;
  toggleModal: () => void;
  accountHolderIndex: number;
}> = ({ show, toggleModal, accountHolderIndex }) => {
  const initialPhoneUpdateValues: PhoneUpdateValueProps = {
    phoneValue: '',
    phoneConfirm: '',
    phoneType: '',
  };
  const accountId = useStateSelector(s => s.accounts.account && s.accounts.account.AccountId);
  const dispatch = useDispatch();
  const accountDetails = useStateSelector(s => s.accountManagement.accountDetails.data);
  const { homePhone, workPhone, mobilePhone } = accountDetails;
  const updateAccountResponseCode = useStateSelector(s => s.accountManagement.update.responseCode);
  const isUpdatingAccountData = useStateSelector(s => s.accountManagement.update.isLoading);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const twoFactorAuthenticationDetails = useStateSelector(s => s.twoFactorAuthentication.details);
  const [showTwoFactorAuthenticationAlert, setShowTwoFactorAuthenticationAlert] = useState<boolean>(false);
  const formikRef = (useRef<FormikProps<PhoneUpdateValueProps> | null>(null)) as ((instance: any) => void) & MutableRefObject<FormikProps<PhoneUpdateValueProps> | null>;

  let header = '';

  const onValidate = (values: PhoneUpdateValueProps) => {
    if (
      twoFactorAuthenticationDetails &&
      values.phoneType === twoFactorAuthenticationDetails.smsVerifiedPhoneType &&
      accountHolderIndex === 0
    ) {
      setShowTwoFactorAuthenticationAlert(true);
    } else {
      setShowTwoFactorAuthenticationAlert(false);
    }

    const { phoneValue, phoneConfirm, phoneType } = values;
    if (!phoneValue || !phoneConfirm || !phoneType) {
      return {
        [`phoneValue`]: getRequiredError(phoneValue),
        [`phoneConfirm`]: getRequiredError(phoneConfirm),
        [`phoneType`]: getRequiredError(phoneType),
      };
    }

    if (phoneValue !== phoneConfirm) {
      return {
        [`phoneConfirm`]: getMismatchError(phoneConfirm === phoneValue),
      };
    }

    if (phoneValue.length < 10 || phoneConfirm.length < 10) {
      return {
        [`phoneValue`]: getInvalidError(phoneValue.length > 10),
        [`phoneConfirm`]: getInvalidError(phoneConfirm.length > 10),
      };
    }

    //Validation based on backend phone number limits
    if (phoneValue.length > 50 || phoneConfirm.length > 50) {
      return {
        [`phoneValue`]: getInvalidError(phoneValue.length < 50),
        [`phoneConfirm`]: getInvalidError(phoneConfirm.length < 50),
      };
    }

    if (phoneType === 'Work' && phoneValue === workPhone) {
      return {
        [`phoneConfirm`]: getStaleError(phoneValue !== workPhone),
      };
    }

    if (phoneType === 'Mobile' && phoneValue === mobilePhone) {
      return {
        [`phoneConfirm`]: getStaleError(phoneValue !== mobilePhone),
      };
    }

    if (phoneType === 'Home' && phoneValue === homePhone) {
      return {
        [`phoneConfirm`]: getStaleError(phoneValue !== homePhone),
      };
    }
  };

  const onSubmit = (values: PhoneUpdateValueProps) => {
    const { phoneValue, phoneType } = values;
    const updateValues: SubmitPhoneValues = {
      item: 'PhoneNumber',
      accountHolder: {
        phones: [
          {
            phoneNumber: phoneValue,
            type: phoneType,
          },
        ],
      },
      holder: accountHolderIndex,
    };
    dispatch(
      AccountManagementDataActions.requestMakeAccountUpdate({
        accountId: accountId,
        request: updateValues,
      })
    );
  };

  useEffect(() => {
    if (updateAccountResponseCode === 0 && show) {
      formikRef && formikRef.current && formikRef.current.resetForm();
      toggleModal();
      dispatch(AccountManagementDataActions.resetMakeAccountUpdateResponseCode());
      setShowTwoFactorAuthenticationAlert(false);
    }
  }, [updateAccountResponseCode, show]);

  const onClose = (resetForm: (nextState?: Partial<FormikState<PhoneUpdateValueProps>> | undefined) => void) => {
    if (show) {
      resetForm();
      dispatch(AccountManagementDataActions.resetMakeAccountUpdateResponseCode());
      setShowTwoFactorAuthenticationAlert(false)
    }
  };

  useEffect(() => {
    if (updateAccountResponseCode === 213) {
      setErrorMsg('The phone number is currently verified for two-factor authentication (2FA). Please add another two-factor authentication method before updating your phone number as we require at least one verified two-factor authentication method at all times.');
    } else {
      setErrorMsg(null);
    }
  }, [updateAccountResponseCode]);

  return (
    <Formik
      initialValues={initialPhoneUpdateValues}
      onSubmit={onSubmit}
      validate={onValidate}
      validateOnChange={true}
      innerRef={formikRef}
    >
      {({ handleSubmit, resetForm }) => (
        <Modal
          setShow={() => {
            onClose(resetForm);
            toggleModal();
          }}
          show={show}
        >
          <BootstrapModal.Header closeButton>
            <BootstrapModal.Title>Phone Number Update</BootstrapModal.Title>
          </BootstrapModal.Header>
          <BootstrapModal.Body>
            <h6>{header}</h6>
            <form id="phoneUpdate" onSubmit={handleSubmit} className="phoneUpdate">
              <FormGroup>
                <Field component={SelectDropdownField} id={`phoneType`} options={AllPhoneTypes} label={'Phone Type'} />
              </FormGroup>
              <>
                <FormGroup>
                  <Field
                    component={TextboxField}
                    name={'phoneValue'}
                    id={'phoneValue'}
                    label="Enter new phone number"
                  />
                </FormGroup>
                <FormGroup>
                  <Field
                    component={TextboxField}
                    name={'phoneConfirm'}
                    id={'phoneConfirm'}
                    label="Confirm new phone number"
                  />
                </FormGroup>
              </>
            </form>
            <p className="txt-sm mute">
              All updates are pending admin review. You will be notified when your submission has been processed.
            </p>
            {
              !twoFactorAuthenticationDetails && accountHolderIndex === 0 &&
              (
                <p className="mute txt-sm warning mb-0">If this phone number is used for two-factor authentication (2FA), please re-verify two-factor authentication after changing the number.</p>
              )
            }
            {showTwoFactorAuthenticationAlert && <p className="mute txt-sm warning mb-0">The phone number is currently verified for two-factor authentication (2FA). Once your phone number change request is complete, you will need to re-verify two-factor authentication with your new number.</p>}
          </BootstrapModal.Body>
          <BootstrapModal.Footer className="txt-sm flex-direction-col">
            <div className="d-flex align-items-center justify-content-space-between w-100">
              <Button
                variant="secondary"
                onClick={() => {
                  resetForm();
                  toggleModal();
                  dispatch(AccountManagementDataActions.resetMakeAccountUpdateResponseCode());
                  setShowTwoFactorAuthenticationAlert(false);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={(values: any) => handleSubmit(values)}
                disabled={isUpdatingAccountData}
              >
                Submit
              </Button>
            </div>
            {errorMsg && <p className="mt-3 mb-0 error w-100 text-align-center">{errorMsg}</p>}
          </BootstrapModal.Footer>
        </Modal>
      )}
    </Formik>
  );
};
